import {
  CLIENT_NODE_BACKGROUND_SYNC_TAG,
  createBackgroundSyncSignal,
  createBrowserHomeNodeCandidateStore,
  createBrowserInstallEnvironment,
  createBrowserKernelHost,
  createBrowserMediaPreferences,
  createBrowserThemeEnvironment,
  fetchSkinCssText,
  listenForBackgroundSyncRequests,
  registerBrowserBackgroundSync,
} from "@econ-v1/adapters-web";
import {
  registerBrowserServiceWorker,
  listenBrowserServiceWorkerMessages,
  reportBrowserShellHealthy,
} from "@econ-v1/adapters-web/service-worker";
import { MessagePortKernelRpcClient } from "@econ-v1/rpc";
import {
  applySkinStyleSheet,
  configureInstallEnvironment,
  configureMediaPreferences,
  configureThemeEnvironment,
  getSkinGlobalSheet,
  INSTALL_LANDING_TAG,
  markSkinPageReady,
  registerNodeUiElements,
  SKIN_CSS_URL,
  type NodeAppShellElement,
  type NodeInstallLandingElement,
} from "@econ-v1/ui";

import { CONTINUE_IN_BROWSER_KEY, shouldBootShell } from "./boot-gate.js";
import { createP0KernelRuntime } from "./kernel-runtime.js";
import { applyDiscoveredNodeCandidate } from "./node-discovered.js";
import { installSessionLifecycle, type ClientSession } from "./session-lifecycle.js";

registerNodeUiElements();
// #1681: `client/ui`'s dark-mode boot script (`shell/theme-init.ts`) may not call
// `localStorage`/`matchMedia` itself (boundary rules B2/B3) — it reads a module-level
// `ThemeEnvironmentPort` singleton instead. Register the real browser-backed implementation
// here, before `<node-app-shell>` is ever inserted into the document (see the `replaceChildren`
// call below), so `initTheme`'s anti-FOUC boot resolution — which runs synchronously inside
// `connectedCallback`, before the kernel connection even exists — reads the real environment
// on its very first call. Mirrors the `createBrowserKernelHost`/`fetchSkinCssText` pattern:
// composition root constructs the adapter, `client/ui` only ever sees the port.
configureThemeEnvironment(createBrowserThemeEnvironment());
// #1687: same seam, same reason, for the two NON-theme media queries the shell reads —
// `(prefers-reduced-motion: reduce)` (the waves stage's hero typing + dot-lattice canvas)
// and `(hover: hover) and (pointer: fine)` (`shell/pmenu.ts`'s hover-open affordance).
// Both are one-shot reads taken during `connectedCallback`/`mount`, so this must likewise
// be registered before the shell is inserted below. See `MediaPreferencesPort`'s own doc
// comment for why this is a separate port from `ThemeEnvironmentPort` rather than two more
// methods on it.
configureMediaPreferences(createBrowserMediaPreferences());
// #1815 (stage 3 of 3): registered as early as possible, before anything else below —
// `createBrowserInstallEnvironment()` attaches the `beforeinstallprompt` listener that
// captures Chromium's deferred install prompt, and that event fires at most once per page
// load, arbitrarily early. Anything that delayed this registration (the skin fetch, the
// kernel/service-worker boot below) risks losing the prompt before `<node-install-landing>`
// or the shell ever gets a chance to call `installEnv.promptInstall()`. Mirrors the
// `configureThemeEnvironment`/`configureMediaPreferences` pattern immediately above: the
// composition root constructs the browser-backed adapter, `client/ui` only ever sees the
// injected `InstallEnvironmentPort`.
const installEnv = createBrowserInstallEnvironment();
configureInstallEnvironment(installEnv);
// lan-direct-pairing plan, C3: the shared persistence seam for a discovered-and-verified
// LAN home-node candidate. One instance for the whole page lifetime — `#handleNodeDiscovered`
// below writes to it when the onboarding discovery screen's `node-discovered` event fires,
// and `createP0KernelRuntime` (via `startClientSession`) reads it fresh on every boot to
// decide what `kernel-runtime.ts`'s `resolveInitialHomeNode` seeds as the home node. Both
// sides ultimately hit the same `localStorage` key regardless of instance identity, but
// sharing one instance here avoids constructing a second `BrowserHomeNodeCandidateStore` for
// no reason.
const homeNodeCandidateStore = createBrowserHomeNodeCandidateStore();
// Document-level tokens + fonts (U2): adopted once, before the shell mounts, so every
// shadow root in the page — the shell's own, and every per-stage root — inherits
// the skin's `:root`-scoped custom properties and registered `@font-face`s without each
// root needing its own copy. `markSkinPageReady` pairs with the FOUC guard `fontsCss`
// carries (`html:not(.page-ready) body{visibility:hidden!important}`) — see
// `skin-stylesheet.ts`'s `markSkinPageReady` doc comment for why this call is required,
// not optional, once fonts are adopted at the document level.
document.adoptedStyleSheets = [...document.adoptedStyleSheets, getSkinGlobalSheet()];
markSkinPageReady();
// #1528 (CSS-out-of-bootstrap): the ~83KB gzip of vendored skin CSS (`skin.css`) no longer
// ships inlined in this bundle — it's fetched here, concurrently with the kernel/wasm boot
// below, and awaited immediately before the shell is inserted into the document (not
// before — see that `await` site below for why). Starting the fetch this early lets it
// overlap with the rest of startup instead of serializing entirely after it. `fetchSkinCssText`
// (the adapter that owns the actual `fetch()` call + its one retry, see its own doc comment)
// lives in `@econ-v1/adapters-web` because `client/ui` package code may not perform
// browser I/O directly (boundary rules B2/B3/N1) — this composition root wires the two
// together, matching the `registerBrowserServiceWorker`/`createBrowserKernelHost` pattern
// already used below.
const skinReady = fetchSkinCssText(SKIN_CSS_URL).then(applySkinStyleSheet);
void registerBrowserServiceWorker("/service-worker.js").then((status) => {
  if (status === "registered") return;
  const notice = document.querySelector<HTMLElement>("#offline-support-status");
  if (notice === null) return;
  notice.hidden = false;
  notice.dataset.serviceWorkerStatus = status;
  notice.textContent = status === "unsupported"
    ? "Offline support is unavailable in this browser. The shell remains available while online."
    : "Offline support could not start. Reload the page or check browser storage permissions.";
});

/**
 * Builds one complete, self-contained client stack and returns it with an idempotent
 * teardown. #1680: this used to be straight-line code in the module body, which made the
 * stack un-rebuildable — there was exactly one of everything, torn down for good on the
 * first `pagehide`. Everything a session owns (kernel host, RPC client, background-sync
 * channel and its listener, the service-worker message bridge, the shell element itself)
 * is created here and released by `teardown`, so a bfcache restore can simply build
 * another one. See `session-lifecycle.ts` for why restore rebuilds rather than preserves.
 *
 * Deliberately NOT re-created per session: the service-worker registration above, which is
 * a document-level, browser-persisted concern unaffected by freeze/restore.
 */
function startClientSession(): (ClientSession & { readonly mount: () => void }) | undefined {
  let backgroundSync: ReturnType<typeof createBackgroundSyncSignal> | undefined;
  let stopBackgroundSyncListener: (() => void) | undefined;
  try {
    backgroundSync = createBackgroundSyncSignal();
    stopBackgroundSyncListener = listenForBackgroundSyncRequests((tag) => {
      void registerBrowserBackgroundSync(tag);
    });
    const ownedBackgroundSync = backgroundSync;
    const kernelHost = createBrowserKernelHost({
      channelName: "client-node-kernel",
      // WebKit cannot persist non-extractable CryptoKeys from a SharedWorker.
      // The elected leader tab remains the single kernel/identity owner on every browser.
      forceLeaderTab: true,
      runtimeFactory: () =>
        createP0KernelRuntime(globalThis.location.origin, {
          backgroundSync: ownedBackgroundSync,
          homeNodeCandidateStore,
        }),
      sharedWorkerUrl: "/kernel.worker.js",
      workerName: "client-node-kernel",
    });
    const onServiceWorkerMessage = (event: MessageEvent<unknown>): void => {
      if (!isBackgroundSyncWake(event.data)) return;
      void kernelHost.wake().catch(() => undefined);
    };
    const stopServiceWorkerMessages = listenBrowserServiceWorkerMessages(onServiceWorkerMessage);
    document.documentElement.dataset.kernelOwner = kernelHost.mode;
    const client = new MessagePortKernelRpcClient(kernelHost.connect());
    const shell = document.createElement("node-app-shell") as NodeAppShellElement;
    shell.addEventListener("node-shell-ready", () => {
      void reportBrowserShellHealthy(import.meta.url);
    }, { once: true });
    // lan-direct-pairing plan, C3: the onboarding discovery screen's manual-address flow
    // (`client/ui/src/shell/onboarding/screens/connect-discovery.ts`) dispatches this
    // `bubbles: true, composed: true` event on a verified candidate's "Continue" — it
    // crosses every shadow boundary between there and here (the onboarding gate's own
    // shadow root, then the shell's), so listening on `shell` itself catches it regardless
    // of how deep the discovery screen is nested. `onboarding-gate.ts`'s own doc comment
    // notes this event is deliberately left unhandled there — persisting it is this
    // composition root's job, not `client/ui`'s (boundary rules B2/B3: `client/ui` may
    // never touch `localStorage` itself). A full reload is the simplest reliable way to
    // make the freshly-stored candidate become the live home node: it re-runs
    // `createP0KernelRuntime`, which re-reads `homeNodeCandidateStore` and — via
    // `kernel-runtime.ts`'s `resolveInitialHomeNode` — seeds the discovered LAN candidate
    // as the boot-time home node instead of whatever `location.origin` would otherwise
    // resolve to. No data loss: this only ever fires before a home node is established, so
    // there is nothing in-memory to lose.
    shell.addEventListener("node-discovered", (event) => {
      applyDiscoveredNodeCandidate(
        (event as CustomEvent<unknown>).detail,
        homeNodeCandidateStore,
        () => globalThis.location.reload(),
      );
    });
    shell.client = client;

    const ownedStopBackgroundSyncListener = stopBackgroundSyncListener;
    let torndown = false;
    let closed: Promise<void> = Promise.resolve();
    return {
      // A FRESH `<node-app-shell>` is mounted per session rather than the previous element
      // being re-pointed at a new client: the element latches `#started` on its first
      // successful start, so reassigning `.client` on a restored shell would install the
      // new client without ever re-running startup — a shell that looks alive and does
      // nothing, which is the very symptom #1680 is about.
      mount: () => {
        document.querySelector("#app")?.replaceChildren(shell);
      },
      teardown: async () => {
        // Idempotent by contract (see `ClientSession`): `pagehide` can fire for a session
        // that a previous disposer already closed.
        if (torndown) return await closed;
        torndown = true;
        stopServiceWorkerMessages();
        ownedStopBackgroundSyncListener();
        ownedBackgroundSync.close();
        client.close();
        // Awaited, not fire-and-forget: `kernelHost.close()` is what drops the identity
        // Web Lock, and a bfcache restore rebuilds only once this resolves. Voiding it
        // here let the rebuilt kernel race the outgoing one for the lock and stall.
        closed = kernelHost.close();
        return await closed;
      },
    };
  } catch {
    // The browser lacks SharedWorker/Web Locks. Release the partial stack before falling
    // back, so a failed start never leaves a background-sync channel open behind the card.
    stopBackgroundSyncListener?.();
    backgroundSync?.close();
    renderUnsupportedBrowser();
    return undefined;
  }
}

// #1815 (stage 3 of 3): guards `bootShell()` against being invoked twice — once from the
// startup decision below, and (independently) from `<node-install-landing>`'s "Continue in
// browser"/"Open Client Node" affordances via `renderInstallLanding()`'s `onContinue`. Those
// two call sites are mutually exclusive in practice (the startup decision only calls
// `bootShell()` directly when it is NOT rendering the landing), but the flag makes that
// invariant structural rather than relying on the two paths never accidentally overlapping.
let booted = false;

/**
 * Boots the full client stack: `startClientSession()`, awaits the skin before mounting (or
 * falls back to `renderSkinLoadFailure`), and wires the bfcache `pagehide`/`pageshow` pair.
 * Factored out of the module body (#1680's original shape) so it can be invoked BOTH at
 * initial load — when the boot gate decides to skip the install landing entirely — and later,
 * on demand, from the landing's escape hatch. Every behavior below is unchanged from the
 * pre-#1815 straight-line version; only the invocation point moved.
 */
async function bootShell(): Promise<void> {
  if (booted) return;
  booted = true;

  const initialSession = startClientSession();
  if (initialSession === undefined) return;

  // Never mount `<node-app-shell>` before its skin is fetched — the shell shares ONE
  // `getSkinStyleSheet()` singleton across every shadow root it (and every per-stage root)
  // adopts (see that function's own doc comment): mounting first would show a real, if
  // momentary, unstyled flash instead of the always-present `#shell-loading` card already
  // visible in `index.html` while this promise is pending.
  //
  // #1679: this promise is now guaranteed to settle — `fetchSkinCssText` bounds each of its
  // two attempts with an `AbortSignal`, so a stalled response reaches the rejection handler
  // below instead of leaving this `await` pending forever.
  let mounted = false;
  await skinReady.then(
    () => {
      initialSession.mount();
      mounted = true;
    },
    (error: unknown) => {
      // `fetchSkinCssText()` already retried once internally (see its own doc comment) —
      // a second failure means the skin genuinely cannot be fetched (e.g. no network and no
      // service-worker cache yet, the exact scenario the CSS-out-of-bootstrap #1528 offline/
      // blocked-asset E2E spec exercises). Render a fallback that depends on NONE of the
      // skin's shadow-scoped classes (`.node-card`/`.node-nav-link`/etc, all vendored into
      // `skin/css/shell-utility.css`, part of the sheet that just failed to load) — by never
      // mounting `<node-app-shell>` here, its own internal `#renderStartupFallback` path
      // (which DOES use those classes) is never reachable until the skin sheet is already
      // populated, so nothing needs to move into the always-synchronous
      // `getSkinGlobalSheet()` slice for that path to stay legible. This fallback instead
      // reads off `index.html`'s own always-present page-level dark theme (`body{background:
      // #09111f;color:#eef3ff}`) for its color/contrast, staying readable with zero adopted
      // stylesheets.
      renderSkinLoadFailure(error);
    },
  );

  // #1680: persistent `pagehide`/`pageshow` pair — teardown on every freeze (releasing the
  // kernel Web Lock, so a bfcached predecessor can never latch a `writer-conflict` against
  // its successor, cf. #1660), rebuild on every persisted restore. Installed only once the
  // shell actually mounted: if the skin failed above, the fallback card owns the page and
  // carries its own Retry affordance, which a silent rebuild would clobber.
  if (mounted) {
    installSessionLifecycle({
      restart: () => {
        // The skin is already applied to `document.adoptedStyleSheets`, which survives the
        // freeze with the rest of the document — no refetch, so restore stays instant.
        const restored = startClientSession();
        restored?.mount();
        return restored;
      },
      session: initialSession,
      target: globalThis,
    });
  }
}

/**
 * Renders `<node-install-landing>` into `#app` for a visitor who has not installed (and has
 * not previously chosen to continue in browser). Deliberately does NOT call
 * `startClientSession()` — a visitor here is only deciding whether to install, so there is no
 * reason to spin up the kernel/SharedWorker/RPC client yet; that cost is deferred until
 * `bootShell()` actually runs, either because the visitor installs and relaunches standalone,
 * or because they click through the escape hatch below.
 *
 * Still awaits `skinReady` first — the landing renders through the shell's shared token sheet
 * (`getSkinGlobalSheet()`/`applySkinStyleSheet()`) like every other shadow root in this page —
 * and reuses the exact same `renderSkinLoadFailure` fallback `bootShell()` uses on rejection,
 * so a network-less first visit degrades identically whichever path startup took.
 */
async function renderInstallLanding(): Promise<void> {
  await skinReady.then(
    () => {
      const landing = document.createElement(INSTALL_LANDING_TAG) as NodeInstallLandingElement;
      landing.installEnvironment = installEnv;
      landing.onContinue = () => {
        // Best-effort: a `sessionStorage` write can throw under strict privacy modes
        // (Safari private browsing, some content blockers). Losing the flag only means the
        // visitor sees the landing again on their next navigation this session — never a
        // reason to block booting the shell they just asked for.
        try {
          sessionStorage.setItem(CONTINUE_IN_BROWSER_KEY, "1");
        } catch {
          // Ignored — see comment above.
        }
        void bootShell();
      };
      // `<node-install-landing>` flips to its own "success" view on `appinstalled` (Task 2,
      // `InstallLandingController`) without any reload or re-render from here.
      document.querySelector("#app")?.replaceChildren(landing);
    },
    (error: unknown) => {
      renderSkinLoadFailure(error);
    },
  );
}

/** `sessionStorage.getItem` guarded against the same strict-privacy-mode throw as the write
 * in `renderInstallLanding()`'s `onContinue` — read failures just mean the escape hatch never
 * "stuck", which resolves to showing the install landing again (the safe default). */
function safeSessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

const bootShellNow = shouldBootShell({
  continuedInBrowser: safeSessionFlag(CONTINUE_IN_BROWSER_KEY),
  hasShellOverride: new URLSearchParams(location.search).has("shell") ||
    new URLSearchParams(location.search).has("onboarding"),
  isStandalone: installEnv.isStandalone(),
});
if (bootShellNow) {
  await bootShell();
} else {
  await renderInstallLanding();
}

function renderUnsupportedBrowser(): void {
  const fallback = document.createElement("main");
  fallback.dataset.clientNodeError = "unsupported-browser";
  fallback.setAttribute("role", "alert");
  fallback.textContent = "Client Node needs SharedWorker or Web Locks support. Update this browser and try again.";
  document.querySelector("#app")?.replaceChildren(fallback);
}

function isBackgroundSyncWake(value: unknown): value is { readonly tag?: string; readonly type: "client-node.background-sync" } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return record.type === "client-node.background-sync" &&
    (record.tag === undefined || record.tag === CLIENT_NODE_BACKGROUND_SYNC_TAG);
}

function renderSkinLoadFailure(error: unknown): void {
  console.error("client-node: skin stylesheet failed to load", error);
  const fallback = document.createElement("main");
  fallback.dataset.clientNodeError = "skin-unavailable";
  fallback.setAttribute("role", "alert");
  fallback.style.boxSizing = "border-box";
  fallback.style.margin = "0 auto";
  fallback.style.maxWidth = "32rem";
  fallback.style.padding = "1.5rem";
  const heading = document.createElement("h1");
  heading.style.margin = "0 0 0.35rem";
  heading.style.fontSize = "1.25rem";
  heading.textContent = "Client Node";
  const message = document.createElement("p");
  message.style.margin = "0 0 1rem";
  message.textContent = "The shell's stylesheet could not be loaded. Check your connection, then retry.";
  const retryButton = document.createElement("button");
  retryButton.type = "button";
  retryButton.textContent = "Retry";
  retryButton.addEventListener("click", () => globalThis.location.reload(), { once: true });
  fallback.append(heading, message, retryButton);
  document.querySelector("#app")?.replaceChildren(fallback);
}
